import MainLayout from "../components/MainLayout/MainLayout";
import Typography from "../components/Typography";
import Button from "../components/Button";
import Separator from "../components/Separator";
import {
    defaultPageIndex,
    defaultPageSize,
    usePaginatedEmissionsSources,
} from "../api/emissions-sources";
import ContentContainer from "../components/ContentContainer";
import MaterialEmissionsSourcesTable from "../components/MaterialEmissionsSourcesTable";
import EmissionsSourcesFilter from "../components/EmissionsSourcesFilter";
import { useStateParams } from "../hooks/useStateParams";
import NotMaterialEmissionsSourcesTable from "../components/NotMaterialEmissionsSourcesTable";
import { Link, useNavigate } from "react-router-dom";
import { appPaths } from "../Routes";
import { EmissionsSourceOrderBy } from "../api/models/EmissionsSource";
import Pagination from "../components/Pagination";

type SelectedTab = "material" | "not-material";

const isValidSelectedTab = (value: string): value is SelectedTab => {
    return value === "material" || value === "not-material";
};

const EmissionsSourcesPage = () => {
    const navigate = useNavigate();

    const [selectedTab, setSelectedTab] = useStateParams<SelectedTab>(
        "material",
        "selectedTab",
        (s) => s,
        (s) => (isValidSelectedTab(s) ? s : "material")
    );

    const navigateToEmissionsSourceDetail = (id: number) => {
        navigate(appPaths.emissionsSourceDetail(id.toString()));
    };

    const emissionsSourceResponse = usePaginatedEmissionsSources({
        isMaterialOverride: selectedTab === "material",
    });

    const handleSelectedTabChange = (tab: SelectedTab) => {
        setSelectedTab(tab);

        tab === "not-material"
            ? emissionsSourceResponse.setIsMaterial(false)
            : emissionsSourceResponse.setIsMaterial(true);
        emissionsSourceResponse.setPageNumber(defaultPageIndex);
        emissionsSourceResponse.setPageSize(defaultPageSize);

        // Below resets filters on selected tab change
        emissionsSourceResponse.setSearch("");
        emissionsSourceResponse.setLocationIds([]);
        emissionsSourceResponse.setScopes([]);
        emissionsSourceResponse.setItem([]);
        emissionsSourceResponse.setType([]);
        emissionsSourceResponse.setCategory([]);
        emissionsSourceResponse.setDataStatus([]);
    };

    const handleOnOrderBy = (newOrderBy: string) => {
        if (newOrderBy === emissionsSourceResponse.orderBy) {
            emissionsSourceResponse.setSortOrder(
                emissionsSourceResponse.sortOrder === "asc" ? "desc" : "asc"
            );
        } else {
            emissionsSourceResponse.setOrderBy(
                newOrderBy as EmissionsSourceOrderBy
            );
            emissionsSourceResponse.setSortOrder("asc");
        }
    };

    return (
        <MainLayout>
            <div className="py-8 px-5 flex flex-col gap-6 md:gap-0 md:flex-row md:items-center md:justify-between">
                <Typography
                    variant="display-small"
                    component="h1"
                    className="text-deep-ocean-20"
                >
                    Assets
                </Typography>
                <Link to={appPaths.emissionsSourceCreate}>
                    <Button className="w-full">Add asset</Button>
                </Link>
            </div>
            <div className="flex space-x-4 p-5">
                <Button
                    variant={selectedTab === "material" ? "primary" : "outline"}
                    onClick={() => handleSelectedTabChange("material")}
                >
                    Material
                </Button>
                <Button
                    variant={
                        selectedTab === "not-material" ? "primary" : "outline"
                    }
                    onClick={() => handleSelectedTabChange("not-material")}
                >
                    Not Material
                </Button>
            </div>
            <Separator />
            <EmissionsSourcesFilter
                hideDataStatusFilter={selectedTab === "not-material"}
                {...emissionsSourceResponse}
            />
            {emissionsSourceResponse.isLoading ? (
                <ContentContainer className="py-6">
                    <div className="flex-1 flex justify-center items-center">
                        <div className="flex flex-col items-center">
                            <p className="mt-2 opacity-60">Loading...</p>
                        </div>
                    </div>
                </ContentContainer>
            ) : (
                <>
                    {emissionsSourceResponse.emissionsSources.length === 0 ? (
                        <ContentContainer className="py-6">
                            <div className="flex-1 flex justify-center items-center">
                                <div className="flex flex-col items-center">
                                    <p className="mt-2 opacity-60">
                                        No existing assets.
                                    </p>
                                </div>
                            </div>
                        </ContentContainer>
                    ) : (
                        <div className="p-6 mt-2">
                            {selectedTab === "material" ? (
                                <>
                                    <MaterialEmissionsSourcesTable
                                        emissionSources={
                                            emissionsSourceResponse.emissionsSources
                                        }
                                        orderBy={
                                            emissionsSourceResponse.orderBy
                                        }
                                        onOrderBy={handleOnOrderBy}
                                        sortOrder={
                                            emissionsSourceResponse.sortOrder
                                        }
                                        onRowClick={
                                            navigateToEmissionsSourceDetail
                                        }
                                    />
                                    <Pagination {...emissionsSourceResponse} />
                                </>
                            ) : (
                                <>
                                    <NotMaterialEmissionsSourcesTable
                                        emissionSources={
                                            emissionsSourceResponse.emissionsSources
                                        }
                                        orderBy={
                                            emissionsSourceResponse.orderBy
                                        }
                                        onOrderBy={handleOnOrderBy}
                                        sortOrder={
                                            emissionsSourceResponse.sortOrder
                                        }
                                        onRowClick={
                                            navigateToEmissionsSourceDetail
                                        }
                                    />
                                    <Pagination {...emissionsSourceResponse} />
                                </>
                            )}
                        </div>
                    )}
                </>
            )}
        </MainLayout>
    );
};

export default EmissionsSourcesPage;
